import { Button } from '@startupquickstart/frontend';
import { RotateCcw } from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  generateShuffledTiles,
  getMovableTiles,
  isPuzzleSolved,
} from './minigame-logic';
import { DemoNotConfigured } from './DemoChrome';
import type { DemoChallenge } from './types';

const BOARD_PX = 360;

/** Web replica of the mobile sliding-puzzle (preview only). */
export function PuzzleDemo({ challenge }: { challenge: DemoChallenge }) {
  const imageUrl = challenge.imageUrl ?? '';
  // Clamp to the supported 3×3 / 4×4 grids.
  const gridSize = challenge.puzzleGridSize === 4 ? 4 : 3;

  const [tiles, setTiles] = useState<number[]>(() => generateShuffledTiles(gridSize));
  const [moves, setMoves] = useState(0);
  const solved = isPuzzleSolved(tiles);
  const movable = useMemo(() => getMovableTiles(tiles, gridSize), [tiles, gridSize]);

  if (!imageUrl) {
    return (
      <DemoNotConfigured>
        This puzzle has no image — a puzzle challenge needs an image to assemble.
      </DemoNotConfigured>
    );
  }

  const tilePx = BOARD_PX / gridSize;

  const handleTileClick = (index: number) => {
    if (solved || !movable.has(index)) return;
    const blankIndex = tiles.indexOf(gridSize * gridSize - 1);
    const next = [...tiles];
    [next[index], next[blankIndex]] = [next[blankIndex], next[index]];
    setTiles(next);
    setMoves((m) => m + 1);
  };

  const reset = () => {
    setTiles(generateShuffledTiles(gridSize));
    setMoves(0);
  };

  const blank = gridSize * gridSize - 1;

  return (
    <div className="flex flex-col items-center gap-4">
      <div className="flex w-full max-w-[360px] items-center justify-between">
        <span className="text-sm font-medium text-muted-foreground">
          {moves} {moves === 1 ? 'move' : 'moves'}
        </span>
        {!solved && (
          <Button variant="ghost" size="sm" onClick={reset}>
            <RotateCcw className="h-3.5 w-3.5" /> Reset
          </Button>
        )}
      </div>

      <div
        className="relative overflow-hidden rounded-lg bg-black/10"
        style={{ width: BOARD_PX, height: BOARD_PX }}
      >
        {tiles.map((tileValue, index) => {
          if (tileValue === blank && !solved) return null;
          const row = Math.floor(index / gridSize);
          const col = index % gridSize;
          const sourceRow = Math.floor(tileValue / gridSize);
          const sourceCol = tileValue % gridSize;
          const isMovable = movable.has(index);
          return (
            <button
              key={tileValue}
              type="button"
              onClick={() => handleTileClick(index)}
              disabled={solved || !isMovable}
              className="absolute overflow-hidden rounded-[4px] transition-transform duration-150 ease-out"
              style={{
                width: tilePx - 2,
                height: tilePx - 2,
                transform: `translate(${col * tilePx}px, ${row * tilePx}px)`,
                border: solved ? 'none' : `1px solid ${isMovable ? 'hsl(var(--primary))' : 'rgba(255,255,255,0.2)'}`,
              }}
            >
              <img
                src={imageUrl}
                alt=""
                draggable={false}
                className="max-w-none"
                style={{
                  width: BOARD_PX,
                  height: BOARD_PX,
                  position: 'absolute',
                  left: -sourceCol * tilePx,
                  top: -sourceRow * tilePx,
                  objectFit: 'cover',
                }}
              />
            </button>
          );
        })}
      </div>

      {solved && (
        <p className="text-base font-bold text-emerald-600">Puzzle solved!</p>
      )}
    </div>
  );
}
